import React from 'react';
import { graphql } from 'gatsby';

import { filterSection } from '../utils/utils';
import { SEO } from '../components';

import {
  FeaturedDocuments,
  FAQsSection,
  HeroSection,
  HookSection,
  ModulesSection,
  TutorfairReviewCarousel,
} from '../sections';

import { HomepageSchema } from '../constants/schemas';
import QuickLinks from '@src/components/BlockFactory/QuickLinks';
import { CountdownTimer } from '@src/components/UI/FlipClock';

const IndexPage = ({
  data: {
    tutorfairReviews,
    datoCmsPage: homePage,
    allDatoCmsDocument: documents,
    timers,
    questions,
  },
}) => {
  const sectionData = {
    quickLinks: homePage.customContent.find(
      (block) => block.__typename === 'DatoCmsQuickLinkSection',
    ),
    hero: filterSection('customContent', 'hero', homePage),
    info: filterSection('customContent', 'info', homePage),
    hook: filterSection('customContent', 'hook', homePage),
    modules: filterSection('section', 'modules', homePage),
    about: filterSection('section', 'about-author', homePage).section[0],
    faqs: filterSection('section', 'faqs', homePage).section[0],
    featuredDocuments: documents.edges,
  };

  const orderedTimers = [...timers.edges];
  orderedTimers.sort((a, b) => {
    if (a.node.title < b.node.title) {
      return -1;
    }
    if (a.node.title > b.node.title) {
      return 1;
    }
    return 0;
  });

  sectionData.featuredDocuments.sort((a, b) => {
    const aPageOrder = a.node.isfeaturedonhomepageorder;
    const bPageOrder = b.node.isfeaturedonhomepageorder;
    if (aPageOrder === null) return 1;
    if (bPageOrder === null) return -1;
    return aPageOrder - bPageOrder;
  });

  return (
    <>
      <SEO
        canonicalUrl="https://www.mymathscloud.com"
        schema={HomepageSchema}
        title={homePage.seo.title}
        description={homePage.seo.description}
        image={homePage.seo.image}
        seoKeywords={homePage.seoKeywords}
      />

      {sectionData.quickLinks && <QuickLinks block={sectionData.quickLinks} />}

      <HeroSection data={sectionData.hero} />

      <div className="srow centered" style={{ marginBottom: '60px' }}>
        {orderedTimers?.map(({ node: { endDate, title } }) => (
          <div className="column narrow">
            <CountdownTimer endDate={endDate} title={title} />
          </div>
        ))}
      </div>

      <FeaturedDocuments data={sectionData.featuredDocuments} />

      <TutorfairReviewCarousel data={tutorfairReviews} />
      <HookSection data={sectionData.hook} />
      <ModulesSection data={sectionData.modules} />
      <FAQsSection data={sectionData.faqs} />
    </>
  );
};

export default IndexPage;

const query = graphql`
  query IndexQuery {
    datoCmsPage(slug: { eq: "home-page" }) {
      id
      title
      slug
      section {
        name
        section {
          ... on DatoCmsSection {
            id
            title
            subtitle
            description
            image {
              url
              alt
            }
            collapsibleBlock {
              description
              title
            }
          }
          ... on DatoCmsModule {
            id
            title
            slug
            position
            comingSoon
            shortDescription
            description
            image {
              alt
              url
            }
            subCategory {
              name
              slug
            }
          }
        }
      }
      ...CustomContentFragment
      seoKeywords {
        id
        keyword
      }
      seo {
        description
        image {
          url
        }
        title
      }
    }
    allDatoCmsDocument(filter: { isfeaturedonhomepage: { eq: true } }) {
      edges {
        node {
          id
          fileUrl
          hasPassword
          externalUrl
          freeSample
          freeDocument
          description
          name
          title
          keywords
          isfeaturedonhomepage
          isfeaturedonhomepageorder
          previewUrl
          fileDescription
          module {
            slug
          }
          documentCategory {
            id
            name
            slug
          }
        }
      }
    }
    timers: allDatoCmsCountdownTimer(filter: { path: { eq: "home-page" } }) {
      edges {
        node {
          title
          endDate
        }
      }
    }
    questions: allDatoCmsQuestionOfTheDay(filter: { active: { eq: true } }) {
      edges {
        node {
          fileName
          link
          shortDescription
          title
          originalId
          file {
            url
          }
          callToActionText
        }
      }
    }
    tutorfairReviews: allDatoCmsTutorfairReview(
      filter: { shownInHomePage: { eq: true } }
      sort: { fields: reviewSortOrder, order: ASC }
    ) {
      edges {
        node {
          shortDescription
          reviewer
          originalId
          longComment
        }
      }
    }
  }
`;
export { query };
